import { useState, useEffect } from "react";
import { useT, useLocale } from "@/i18n/locale-context";
import {
  getPhotoGuideProfile,
  pickL10n,
  type Subject,
  type ScaleRef,
} from "./photo-guide-profiles";
import macroCoinExample from "@/assets/photo-example-macro-coin.jpg";
import fullbodyExample from "@/assets/photo-example-fullbody.jpg";
import fullbodySetupVideo from "@/assets/photo-example-fullbody-setup.mp4.asset.json";
import faceExample from "@/assets/photo-example-face.jpg";
import scalpExample from "@/assets/photo-example-scalp.jpg";
import smileExample from "@/assets/photo-example-smile.jpg";
import eyesExample from "@/assets/photo-example-eyes.jpg";
import backExample from "@/assets/photo-example-back.jpg";
import thighsExample from "@/assets/photo-example-thighs.jpg";
import bellyExample from "@/assets/photo-example-belly.jpg";
import acneExample from "@/assets/photo-example-acne.jpg";
import oncoExample from "@/assets/photo-example-onco.jpg";
import scarsExample from "@/assets/photo-example-scars.jpg";
import menoAndroExample from "@/assets/photo-example-meno-andro.jpg";
import tattooExample from "@/assets/photo-example-tattoo.jpg";
import sobrietyExample from "@/assets/photo-example-sobriety.jpg";
import androExample from "@/assets/photo-example-andro.jpg";
import piercingExample from "@/assets/photo-example-piercing.jpg";
import melasmaExample from "@/assets/photo-example-melasma.jpg";
import psoriasisExample from "@/assets/photo-example-psoriasis.jpg";
import rosaceaExample from "@/assets/photo-example-rosacea.jpg";
import hairgraftBeforeExample from "@/assets/photo-example-hairgraft-before.jpg";
import hairgraftAfterExample from "@/assets/photo-example-hairgraft-after.jpg";
import beardExample from "@/assets/photo-example-beard.jpg";
import browsExample from "@/assets/photo-example-brows.jpg";
import postureFrontExample from "@/assets/photo-example-posture-front.jpg";
import pregnancyFrontExample from "@/assets/photo-example-pregnancy-front.jpg";

/**
 * Animated, illustrated photo-capture guide shown inside each protocol page
 * and on the capture screen. Pure SVG + CSS — no external assets.
 *
 * Four steps:
 *   1. Lumière      — natural window light, no flash
 *   2. Distance     — phone at ~30 cm, same angle as before
 *   3. Cadrage      — the area fills the frame
 *   4. Échelle      — place a coin or ruler next to the zone for scale
 */
export function PhotoGuide({
  defaultOpen = false,
  goal,
}: {
  defaultOpen?: boolean;
  /** Protocol goal id — drives contextual illustrations and copy. */
  goal?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [step, setStep] = useState(0);
  const t = useT();
  const { locale } = useLocale();
  const profile = getPhotoGuideProfile(goal);
  const dist = pickL10n(profile.distanceLabel, locale);

  // Step 4's title depends on whether a real scale reference is required.
  // For "none" protocols (face, full-body…), the 4th step is about visual
  // consistency between sessions, not a physical scale object.
  const step4TitleKey =
    profile.scaleRef === "none"
      ? "photoguide.step4.consistency.title"
      : "photoguide.step4.title";

  const steps = [
    { title: t("photoguide.step1.title"), body: pickL10n(profile.light, locale),    svg: <LightSvg subject={profile.subject} /> },
    { title: t("photoguide.step2.title"), body: pickL10n(profile.distance, locale), svg: <DistanceSvg subject={profile.subject} distanceLabel={dist} /> },
    { title: t("photoguide.step3.title"), body: pickL10n(profile.framing, locale),  svg: <FramingSvg subject={profile.subject} /> },
    { title: t(step4TitleKey),            body: pickL10n(profile.scale, locale),    svg: <ScaleSvg scaleRef={profile.scaleRef} subject={profile.subject} locale={locale} /> },
  ];

  return (
    <section className="mt-6 rounded-2xl border border-foreground/10 bg-foreground/[0.02] overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {t("photoguide.kicker")}
          </p>
          <p className="font-semibold text-sm mt-0.5">{t("photoguide.title")}</p>
        </div>
        <span className="text-xs text-muted-foreground">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 animate-fade-in">
          {/* Illustration */}
          <div className="rounded-xl bg-background/60 border border-foreground/10 aspect-[16/9] grid place-items-center overflow-hidden">
            {steps[step].svg}
          </div>

          {/* Text */}
          <div className="mt-3 min-h-[3.5rem]">
            <p className="text-sm font-semibold">
              {step + 1}. {steps[step].title}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{steps[step].body}</p>
          </div>

          {/* Dots + nav */}
          <div className="mt-3 flex items-center justify-between">
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  aria-label={`Step ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-foreground" : "w-1.5 bg-foreground/25"}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="text-xs rounded-full px-3 py-1 border border-foreground/15 disabled:opacity-40"
              >
                ←
              </button>
              <button
                onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
                disabled={step === steps.length - 1}
                className="text-xs rounded-full px-3 py-1 border border-foreground/15 disabled:opacity-40"
              >
                →
              </button>
            </div>
          </div>

          {/* Real-world example panel */}
          <ExamplePanel subject={profile.subject} goal={goal} />
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------- */
/* Real example panel — uses real reference image (+ short clip) */
/* -------------------------------------------------------------- */

function ExamplePanel({ subject, goal }: { subject: Subject; goal?: string }) {
  const t = useT();

  // Prefer a protocol-specific reference image when we have one; otherwise
  // fall back to the generic per-subject example. Some protocols expose a
  // pair of references (e.g. female + male for meno/andro, tattoo + piercing).
  const entry = goal ? EXAMPLE_BY_GOAL[goal] : undefined;
  const examples: ExampleAsset[] = entry
    ? (Array.isArray(entry) ? entry : [entry])
    : [getExampleForSubject(subject)];
  // Full-body protocols also get the tripod-setup short clip.
  const showSetupVideo =
    subject === "fullbody" || subject === "back_posture" || subject === "belly";
  const isPair = examples.length > 1;

  // Auto-cycle through examples every 4 s so the user clearly sees every
  // required angle / view (e.g. beard front vs brows, posture front vs back).
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    if (examples.length <= 1) return;
    const id = window.setInterval(
      () => setActiveIdx((i) => (i + 1) % examples.length),
      4000,
    );
    return () => window.clearInterval(id);
  }, [examples.length]);

  return (
    <div className="mt-4 rounded-xl border border-foreground/10 bg-background/60 p-3">
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
        {t("photoguide.example.title")}
      </p>
      <div className={`grid gap-3 ${showSetupVideo ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
        {/* Single slot that cycles through every required view */}
        <div>
          <figure className="m-0 relative">
            <img
              src={examples[activeIdx].src}
              alt={examples[activeIdx].alt}
              loading="lazy"
              width={examples[activeIdx].width}
              height={examples[activeIdx].height}
              className="w-full h-auto rounded-lg border border-foreground/10 object-cover transition-opacity duration-500"
              key={activeIdx}
            />
            {isPair && (
              <span className="absolute top-2 left-2 rounded-full bg-background/85 backdrop-blur px-2 py-0.5 text-[10px] uppercase tracking-widest text-foreground/80">
                {activeIdx + 1} / {examples.length}
              </span>
            )}
            <figcaption className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
              {t(examples[activeIdx].captionKey)}
            </figcaption>
          </figure>
          {isPair && (
            <div className="mt-2 flex items-center justify-center gap-1.5">
              {examples.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIdx(i)}
                  aria-label={`View ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === activeIdx ? "w-5 bg-foreground" : "w-1.5 bg-foreground/25"}`}
                />
              ))}
            </div>
          )}
        </div>
        {showSetupVideo && (
          <figure className="m-0">
            <video
              src={fullbodySetupVideo.url}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-auto rounded-lg border border-foreground/10 aspect-[9/16] object-cover bg-foreground/5"
            />
            <figcaption className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
              {t("photoguide.example.video.caption")}
            </figcaption>
          </figure>
        )}
      </div>
    </div>
  );
}

type ExampleAsset = {
  src: string;
  alt: string;
  width: number;
  height: number;
  captionKey: string;
};

/** Protocol-specific reference photos. Overrides the per-subject fallback.
 *  A value can be a single asset or an array (rendered as a paired grid). */
const EXAMPLE_BY_GOAL: Record<string, ExampleAsset | ExampleAsset[]> = {
  acne:           { src: acneExample,      alt: "Mild inflammatory acne reference photo on cheek and jawline (close-up at ~25 cm)", width: 1088, height: 832, captionKey: "photoguide.example.acne.caption" },
  onco_recovery:  { src: oncoExample,      alt: "Oncology recovery reference photo: face and neck only, soft indirect daylight",     width: 1088, height: 832, captionKey: "photoguide.example.onco.caption" },
  scars:          { src: scarsExample,     alt: "Healed linear scar with 1 euro coin as scale reference",                            width: 1280, height: 832, captionKey: "photoguide.example.scars.caption" },
  meno_andro:     [
    { src: menoAndroExample, alt: "Menopause baseline reference selfie of a mature woman, no makeup",                                width: 1088, height: 832, captionKey: "photoguide.example.meno_andro.caption" },
    { src: androExample,     alt: "Andropause baseline reference selfie of a mature man, no makeup",                                 width: 1088, height: 832, captionKey: "photoguide.example.andro.caption" },
  ],
  tattoo_piercing:[
    { src: tattooExample,    alt: "Healed tattoo close-up with 1 euro coin as scale reference",                                      width: 1280, height: 832, captionKey: "photoguide.example.tattoo.caption" },
    { src: piercingExample,  alt: "Recent ear piercing close-up with 1 euro coin as scale reference",                                width: 1280, height: 832, captionKey: "photoguide.example.piercing.caption" },
  ],
  sobriety:       { src: sobrietyExample,  alt: "Quitting-smoking baseline reference selfie, neutral expression",                    width: 1088, height: 832, captionKey: "photoguide.example.sobriety.caption" },
  melasma:        { src: melasmaExample,   alt: "Real photo of melasma — symmetrical pigmentation on cheekbones and upper lip",       width: 800, height: 900, captionKey: "photoguide.example.melasma.caption" },
  rosacea_reactive:{ src: rosaceaExample,  alt: "Real reference photo of rosacea / reactive skin — diffuse central facial redness with fine capillaries, no makeup, neutral daylight", width: 1088, height: 832, captionKey: "photoguide.example.rosacea.caption" },
  eczema_psoriasis:{ src: psoriasisExample,alt: "Real photo of a psoriasis plaque on the forearm, well demarcated",                  width: 800, height: 1000, captionKey: "photoguide.example.psoriasis.caption" },
  hair_graft:     [
    { src: hairgraftBeforeExample, alt: "Real BEFORE photo of a man with hair loss, front view, neutral overhead light",             width: 800, height: 1000, captionKey: "photoguide.example.hairgraft_before.caption" },
    { src: hairgraftAfterExample,  alt: "Real AFTER photo of the same patient post hair transplant, same framing and angle",         width: 1100, height: 800, captionKey: "photoguide.example.hairgraft_after.caption" },
  ],
  beard_brows:    [
    { src: beardExample, alt: "Real photo of a bearded man, front view close-up at ~25 cm, soft side light",                          width: 1024, height: 1024, captionKey: "photoguide.example.beard.caption" },
    { src: browsExample, alt: "Real photo of a woman framed hairline to upper cheekbones, no makeup, eyebrows visible",               width: 1024, height: 1024, captionKey: "photoguide.example.brows.caption" },
  ],
  posture:        [
    { src: postureFrontExample, alt: "Real photo of a person standing front view, plain background, neutral pose",                    width: 900, height: 1200, captionKey: "photoguide.example.posture_front.caption" },
    { src: backExample,         alt: "Real photo of a person standing back view, plain background, neutral pose",                     width: 832, height: 1280, captionKey: "photoguide.example.posture_back.caption" },
  ],
  pregnancy_postpartum: [
    { src: bellyExample,          alt: "Pregnancy side profile reference photo, chest to hips, plain background",                     width: 832, height: 1088, captionKey: "photoguide.example.pregnancy_side.caption" },
    { src: pregnancyFrontExample, alt: "Real photo of a pregnant woman in front view, plain background",                              width: 900, height: 1200, captionKey: "photoguide.example.pregnancy_front.caption" },
  ],
};

function getExampleForSubject(subject: Subject): ExampleAsset {
  switch (subject) {
    case "face":
      return { src: faceExample, alt: "Face reference photo example", width: 832, height: 1088, captionKey: "photoguide.example.face.caption" };
    case "scalp":
      return { src: scalpExample, alt: "Scalp top-down reference photo with ruler", width: 1088, height: 832, captionKey: "photoguide.example.scalp.caption" };
    case "smile":
      return { src: smileExample, alt: "Smile close-up with white card for shade reference", width: 1088, height: 832, captionKey: "photoguide.example.smile.caption" };
    case "eyes":
      return { src: eyesExample, alt: "Eyes close-up reference photo", width: 1088, height: 832, captionKey: "photoguide.example.eyes.caption" };
    case "back_posture":
      return { src: backExample, alt: "Full back posture reference photo", width: 832, height: 1280, captionKey: "photoguide.example.back.caption" };
    case "thighs":
      return { src: thighsExample, alt: "Thighs back-view reference photo", width: 832, height: 1088, captionKey: "photoguide.example.thighs.caption" };
    case "belly":
      return { src: bellyExample, alt: "Belly side profile reference photo", width: 832, height: 1088, captionKey: "photoguide.example.belly.caption" };
    case "fullbody":
      return { src: fullbodyExample, alt: "Full body reference photo example", width: 832, height: 1280, captionKey: "photoguide.example.fullbody.caption" };
    case "macro":
    default:
      return { src: macroCoinExample, alt: "Macro skin photo with 1 euro coin as scale reference", width: 1280, height: 832, captionKey: "photoguide.example.macro.caption" };
  }
}

/* -------------------------------------------------------------- */
/* Subject silhouettes — drawn around (cx, cy) at the given scale */
/* -------------------------------------------------------------- */

function SubjectIcon({ subject, cx, cy, s = 1 }: { subject: Subject; cx: number; cy: number; s?: number }) {
  const k = (n: number) => n * s;
  switch (subject) {
    case "scalp":
      // Top-down view of a head with a midline parting
      return (
        <g stroke="currentColor" fill="none" strokeWidth="2">
          <ellipse cx={cx} cy={cy} rx={k(38)} ry={k(30)} />
          <line x1={cx} y1={cy - k(28)} x2={cx} y2={cy + k(28)} strokeDasharray="3 3" opacity=".7" />
          {[-1, 1].map((sgn) =>
            [0, 1, 2, 3].map((i) => (
              <path
                key={`${sgn}-${i}`}
                d={`M ${cx + sgn * (4 + i * 7)} ${cy - 18 + i * 10} q ${sgn * 3} 6 ${sgn * 8} 10`}
                opacity=".55"
              />
            )),
          )}
        </g>
      );
    case "macro":
      // Skin patch with a lesion (mole/tattoo blob)
      return (
        <g>
          <rect x={cx - k(46)} y={cy - k(28)} width={k(92)} height={k(56)} rx={k(10)} fill="currentColor" opacity=".08" />
          <ellipse cx={cx} cy={cy} rx={k(16)} ry={k(12)} fill="hsl(var(--destructive))" opacity=".3" stroke="hsl(var(--destructive))" strokeWidth="1.5" />
        </g>
      );
    case "smile":
      return (
        <g stroke="currentColor" fill="none" strokeWidth="2">
          <path d={`M ${cx - k(38)} ${cy} q ${k(38)} ${k(34)} ${k(76)} 0`} />
          {/* teeth */}
          {Array.from({ length: 7 }).map((_, i) => (
            <rect key={i} x={cx - k(30) + i * k(9)} y={cy + k(4)} width={k(7)} height={k(14)} fill="currentColor" opacity=".15" rx="1" />
          ))}
        </g>
      );
    case "fullbody":
      return (
        <g stroke="currentColor" fill="none" strokeWidth="2">
          <circle cx={cx} cy={cy - k(36)} r={k(8)} />
          <line x1={cx} y1={cy - k(28)} x2={cx} y2={cy + k(8)} />
          <line x1={cx - k(14)} y1={cy - k(20)} x2={cx + k(14)} y2={cy - k(20)} />
          <line x1={cx} y1={cy + k(8)} x2={cx - k(10)} y2={cy + k(36)} />
          <line x1={cx} y1={cy + k(8)} x2={cx + k(10)} y2={cy + k(36)} />
        </g>
      );
    case "back_posture":
      return (
        <g stroke="currentColor" fill="none" strokeWidth="2">
          <circle cx={cx} cy={cy - k(34)} r={k(8)} />
          <path d={`M ${cx - k(10)} ${cy - k(24)} q ${k(10)} ${k(8)} ${k(20)} 0 L ${cx + k(10)} ${cy + k(12)} q ${-k(10)} ${k(6)} ${-k(20)} 0 Z`} opacity=".4" fill="currentColor" />
          {/* spine alignment line */}
          <line x1={cx} y1={cy - k(26)} x2={cx} y2={cy + k(36)} stroke="hsl(var(--accent))" strokeDasharray="3 3" />
        </g>
      );
    case "thighs":
      return (
        <g stroke="currentColor" fill="none" strokeWidth="2">
          <path d={`M ${cx - k(22)} ${cy - k(20)} q ${k(22)} ${-k(6)} ${k(44)} 0 L ${cx + k(18)} ${cy + k(30)} q ${-k(18)} ${k(4)} ${-k(36)} 0 Z`} fill="currentColor" opacity=".1" />
          <line x1={cx} y1={cy - k(16)} x2={cx} y2={cy + k(32)} strokeDasharray="3 3" opacity=".5" />
        </g>
      );
    case "belly":
      return (
        <g stroke="currentColor" fill="none" strokeWidth="2">
          <path d={`M ${cx - k(18)} ${cy - k(34)} q ${-k(2)} ${k(20)} ${k(14)} ${k(34)} q ${k(8)} ${-k(6)} ${k(10)} ${-k(18)} q ${-k(2)} ${-k(20)} -${k(14)} -${k(30)} Z`} fill="currentColor" opacity=".1" />
          <circle cx={cx + k(2)} cy={cy + k(4)} r="1.5" fill="currentColor" />
        </g>
      );
    case "eyes":
      return (
        <g stroke="currentColor" fill="none" strokeWidth="2">
          {[-1, 1].map((sgn) => (
            <g key={sgn}>
              <path d={`M ${cx + sgn * k(18) - k(12)} ${cy} q ${k(12)} ${-k(8)} ${k(24)} 0 q ${-k(12)} ${k(8)} ${-k(24)} 0 Z`} />
              <circle cx={cx + sgn * k(18)} cy={cy} r={k(3.5)} fill="currentColor" />
            </g>
          ))}
        </g>
      );
    case "face":
    default:
      return (
        <g stroke="currentColor" fill="none" strokeWidth="2">
          <circle cx={cx} cy={cy} r={k(32)} />
          <circle cx={cx - k(8)} cy={cy - k(4)} r="2" fill="currentColor" />
          <circle cx={cx + k(8)} cy={cy - k(4)} r="2" fill="currentColor" />
          <path d={`M ${cx - k(10)} ${cy + k(12)} q ${k(10)} ${k(8)} ${k(22)} 0`} strokeWidth="1.5" />
        </g>
      );
  }
}

/* -------------------------------------------------------------- */
/* Step illustrations                                              */
/* -------------------------------------------------------------- */

function LightSvg({ subject }: { subject: Subject }) {
  // Overhead light for scalp/hair, side light for macro/skin, diffuse for full-body
  const overhead = subject === "scalp";
  return (
    <svg viewBox="0 0 320 180" className="w-full h-full text-foreground/80">
      {overhead ? (
        <>
          {/* Ceiling lamp */}
          <line x1="160" y1="0" x2="160" y2="24" stroke="currentColor" strokeWidth="1.5" />
          <path d="M140 24 L180 24 L172 40 L148 40 Z" fill="currentColor" opacity=".15" stroke="currentColor" strokeWidth="1.5" />
          {[-1, 0, 1].map((i) => (
            <line key={i} x1={160 + i * 8} y1="44" x2={160 + i * 30} y2="80"
              stroke="currentColor" strokeWidth="1" opacity=".35"
              style={{ animation: `fade-in 1.4s ease-in-out ${i * 0.15}s infinite alternate` }} />
          ))}
        </>
      ) : (
        <>
          {/* Window */}
          <rect x="14" y="28" width="70" height="120" rx="4" fill="none" stroke="currentColor" strokeWidth="2" />
          <line x1="49" y1="28" x2="49" y2="148" stroke="currentColor" strokeWidth="1" opacity=".5" />
          <line x1="14" y1="88" x2="84" y2="88" stroke="currentColor" strokeWidth="1" opacity=".5" />
          {[0, 1, 2, 3].map((i) => (
            <line key={i} x1="84" y1={50 + i * 22} x2="170" y2={70 + i * 14}
              stroke="currentColor" strokeWidth="1" opacity=".3"
              style={{ animation: `fade-in 1.4s ease-in-out ${i * 0.15}s infinite alternate` }} />
          ))}
        </>
      )}
      <SubjectIcon subject={subject} cx={220} cy={95} s={subject === "fullbody" ? 1.3 : 1} />
      {/* No flash icon */}
      <g transform="translate(290,150)">
        <path d="M0 -10 L-6 2 L-1 2 L-3 12 L6 -2 L1 -2 Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <line x1="-12" y1="-12" x2="10" y2="14" stroke="hsl(var(--destructive))" strokeWidth="2" />
      </g>
    </svg>
  );
}

function DistanceSvg({ subject, distanceLabel }: { subject: Subject; distanceLabel: string }) {
  return (
    <svg viewBox="0 0 320 180" className="w-full h-full text-foreground/80">
      <g style={{ animation: "phone-pulse 2s ease-in-out infinite" }}>
        <rect x="36" y="58" width="44" height="74" rx="6" fill="none" stroke="currentColor" strokeWidth="2" />
        <rect x="42" y="66" width="32" height="50" rx="2" fill="currentColor" opacity=".08" />
        <circle cx="58" cy="124" r="2" fill="currentColor" />
      </g>
      <line x1="86" y1="95" x2="184" y2="95" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" />
      <polygon points="184,95 178,91 178,99" fill="currentColor" />
      <polygon points="86,95 92,91 92,99" fill="currentColor" />
      <text x="135" y="88" textAnchor="middle" fontSize="10" fill="currentColor" fontFamily="ui-sans-serif">{distanceLabel}</text>
      <SubjectIcon subject={subject} cx={232} cy={95} s={subject === "fullbody" ? 1.4 : 1} />
      <style>{`@keyframes phone-pulse { 0%,100% { transform: translateX(0); } 50% { transform: translateX(6px); } }`}</style>
    </svg>
  );
}

function FramingSvg({ subject }: { subject: Subject }) {
  return (
    <svg viewBox="0 0 320 180" className="w-full h-full text-foreground/80">
      <rect x="60" y="20" width="200" height="140" rx="8" fill="none" stroke="currentColor" strokeWidth="2" />
      {[
        [70, 30, 1, 1], [250, 30, -1, 1], [70, 150, 1, -1], [250, 150, -1, -1],
      ].map(([x, y, sx, sy], i) => (
        <path key={i}
          d={`M ${x} ${y + (sy as number) * 14} L ${x} ${y} L ${x + (sx as number) * 14} ${y}`}
          fill="none" stroke="hsl(var(--accent))" strokeWidth="2.5"
          style={{ animation: `bracket-pulse 1.6s ease-in-out ${i * 0.1}s infinite` }} />
      ))}
      <SubjectIcon subject={subject} cx={160} cy={90} s={subject === "fullbody" ? 1.7 : subject === "macro" ? 1.3 : 1.6} />
      <style>{`@keyframes bracket-pulse { 0%,100% { opacity: .5; } 50% { opacity: 1; } }`}</style>
    </svg>
  );
}

function ScaleSvg({ scaleRef, subject, locale }: { scaleRef: ScaleRef; subject: Subject; locale?: string }) {
  if (scaleRef === "none") {
    const sameFrameLabel =
      locale === "en" ? "= same frame, same outfit"
      : locale === "nl" ? "= zelfde kader, zelfde outfit"
      : "= même cadre, même tenue";
    // Show "same outfit / same angle" — two side-by-side frames
    return (
      <svg viewBox="0 0 320 180" className="w-full h-full text-foreground/80">
        {[60, 180].map((x, idx) => (
          <g key={idx}>
            <rect x={x} y="30" width="80" height="120" rx="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <SubjectIcon subject={subject} cx={x + 40} cy={92} s={0.85} />
            <text x={x + 40} y="166" textAnchor="middle" fontSize="9" fill="currentColor" opacity=".7" fontFamily="ui-sans-serif">
              {idx === 0 ? "T0" : "T+n"}
            </text>
          </g>
        ))}
        <text x="160" y="22" textAnchor="middle" fontSize="9" fill="currentColor" opacity=".7" fontFamily="ui-sans-serif">
          {sameFrameLabel}
        </text>
      </svg>
    );
  }
  // Coin / Ruler / Card reference
  return (
    <svg viewBox="0 0 320 180" className="w-full h-full text-foreground/80">
      <rect x="40" y="36" width="240" height="108" rx="14" fill="currentColor" opacity=".06" />
      <SubjectIcon subject={subject === "fullbody" ? "macro" : subject} cx={130} cy={90} s={1} />
      <g style={{ animation: "coin-drop 2.4s ease-in-out infinite" }}>
        {scaleRef === "coin" && (
          <g>
            <circle cx="220" cy="90" r="18" fill="none" stroke="currentColor" strokeWidth="2" />
            <text x="220" y="94" textAnchor="middle" fontSize="11" fill="currentColor" fontFamily="ui-sans-serif" fontWeight="600">1€</text>
          </g>
        )}
        {scaleRef === "ruler" && (
          <g>
            <rect x="194" y="74" width="60" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
            {Array.from({ length: 7 }).map((_, i) => (
              <line key={i} x1={196 + i * 10} y1="74" x2={196 + i * 10} y2={i % 5 === 0 ? 84 : 80} stroke="currentColor" strokeWidth="1" />
            ))}
            <text x="224" y="100" textAnchor="middle" fontSize="9" fill="currentColor" fontFamily="ui-sans-serif">cm</text>
          </g>
        )}
        {scaleRef === "card" && (
          <g>
            <rect x="190" y="74" width="60" height="38" rx="3" fill="currentColor" opacity=".05" stroke="currentColor" strokeWidth="2" />
            <text x="220" y="98" textAnchor="middle" fontSize="9" fill="currentColor" fontFamily="ui-sans-serif">carte blanche</text>
          </g>
        )}
      </g>
      <line x1="60" y1="132" x2="260" y2="132" stroke="currentColor" strokeWidth="1.5" />
      {Array.from({ length: 11 }).map((_, i) => (
        <line key={i} x1={60 + i * 20} y1="128" x2={60 + i * 20} y2={i % 5 === 0 ? 138 : 134}
          stroke="currentColor" strokeWidth="1" />
      ))}
      <text x="60" y="150" fontSize="8" fill="currentColor" fontFamily="ui-sans-serif">0</text>
      <text x="155" y="150" fontSize="8" fill="currentColor" fontFamily="ui-sans-serif">5 cm</text>
      <text x="252" y="150" fontSize="8" fill="currentColor" fontFamily="ui-sans-serif">10</text>
      <style>{`@keyframes coin-drop {
        0% { transform: translateY(-30px); opacity: 0; }
        30% { transform: translateY(0); opacity: 1; }
        100% { transform: translateY(0); opacity: 1; }
      }`}</style>
    </svg>
  );
}